import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { UserModel } from 'app/models/user.model';
import { DriverContextService } from 'app/modules/driver-app/services/driver-context.service';
import { DriverRefreshService } from 'app/modules/driver-app/services/driver-refresh.service';
import { DriverSimulationService } from 'app/services/driver-simulation.service';
import { UserService } from 'app/services/user.service';
import { ApiService } from 'app/shared/apis/api.service';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';
import { Subject, take, takeUntil } from 'rxjs';

@Component({
    selector: 'driver-simulation-selector',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, AutoTranslatePipe],
    template: `
        <mat-form-field *ngIf="visible" class="driver-sim-select" [subscriptSizing]="'dynamic'" appearance="outline">
            <mat-label>{{ 'Motorista' | autoTranslate }}</mat-label>
            <mat-select [formControl]="control">
                <mat-option *ngFor="let driver of drivers" [value]="driver.user_id">
                    <span dir="auto">{{ driver.name }}</span>
                </mat-option>
            </mat-select>
        </mat-form-field>
    `,
    styles: [
        `
            :host {
                display: block;
            }

            .driver-sim-select {
                width: 260px;
                max-width: calc(100vw - 2rem);
            }

            :host ::ng-deep .mat-mdc-form-field-flex {
                background: rgba(255, 255, 255, 0.06);
            }

            :host ::ng-deep .mdc-notched-outline__leading,
            :host ::ng-deep .mdc-notched-outline__notch,
            :host ::ng-deep .mdc-notched-outline__trailing {
                border-color: rgba(255, 255, 255, 0.18) !important;
            }

            :host ::ng-deep .mat-mdc-select-value,
            :host ::ng-deep .mat-mdc-select-arrow,
            :host ::ng-deep .mat-mdc-form-field-label {
                color: #e5e7eb !important;
            }
        `,
    ],
})
export class DriverSimulationSelectorComponent implements OnInit, OnDestroy {
    visible = false;
    drivers: UserModel[] = [];
    control = new FormControl<number | null>(null);

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly userService: UserService,
        private readonly driverSimulation: DriverSimulationService,
        private readonly driverContext: DriverContextService,
        private readonly driverRefresh: DriverRefreshService,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        ApiService.userCurrent.pipe(take(1)).subscribe((user) => {
            this.visible = !!user?.is_admin;
            if (!this.visible) return;

            this.userService
                .list({ limit: 'all', profile_type: 'DRIVER' } as any)
                .pipe(take(1))
                .subscribe((res) => {
                    this.drivers = (res?.items || []).sort((a, b) =>
                        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
                    );
                    const current = this.driverSimulation.getSimulatedUserId();
                    if (current) {
                        this.control.setValue(current, { emitEvent: false });
                    } else if (this.drivers.length) {
                        this.control.setValue(this.drivers[0].user_id, { emitEvent: false });
                        this.driverSimulation.setSimulatedDriver(this.drivers[0]);
                    }
                    this.cdr.markForCheck();
                });
        });

        this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((userId) => {
            const driver = this.drivers.find((d) => d.user_id === userId);
            if (!driver) return;
            this.driverSimulation.setSimulatedDriver(driver);
            this.driverContext.refresh();
            this.driverRefresh.refreshAll();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
